import { useEffect, useRef, useState } from 'react'
import { Button } from 'blaze-ui/Button'
import { FolderImport } from 'blaze-ui/icons'
import { CheckBroken } from 'blaze-ui/icons'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
import { ButtonSize, ButtonVariant } from 'blaze-ui/Button/Types'
import { Icon } from 'blaze-ui/icons/Types'
import { LoadingEllipses } from 'components/Wizards/Wizard/Form/SectionHeader'

interface GoogleDriveActionButtonProps {
  isSyncing: boolean
  isPickerLoading: boolean
  openPicker: () => void
  variant?: ButtonVariant
  size?: ButtonSize
  frontIcon?: Icon
  label?: string
}

export const GoogleDriveActionButton = ({
  isSyncing,
  isPickerLoading,
  openPicker,
  size = 'sm',
  variant = 'secondary',
  frontIcon,
  label = 'Import Files',
}: GoogleDriveActionButtonProps) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const [showDone, setShowDone] = useState(false)
  const prevIsSyncingRef = useRef(isSyncing)
  const timeoutRef = useRef<NodeJS.Timeout>()

  useEffect(() => {
    const syncJustCompleted = prevIsSyncingRef.current && !isSyncing
    prevIsSyncingRef.current = isSyncing

    if (syncJustCompleted) {
      setShowDone(true)
      timeoutRef.current = setTimeout(() => {
        setShowDone(false)
      }, 3000)
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [isSyncing])

  if (isSystemRedesignEnabled) {
    if (isSyncing) {
      return (
        <Button variant={variant} size={size} isDisabled>
          Importing
          <LoadingEllipses />
        </Button>
      )
    }

    if (showDone) {
      return (
        <Button variant={variant} size={size} frontIcon={CheckBroken} isDisabled>
          Done!
        </Button>
      )
    }

    return (
      <Button
        variant={variant}
        size={size}
        frontIcon={frontIcon || FolderImport}
        onPress={openPicker}
        isDisabled={isPickerLoading}
      >
        {label}
      </Button>
    )
  }

  if (isSyncing) {
    return (
      <Button variant="ghost" color="var(--dark-80)" size={size} isDisabled>
        Importing
        <LoadingEllipses />
      </Button>
    )
  }

  if (showDone) {
    return (
      <Button variant="ghost" color="var(--dark-80)" size={size} frontIcon={CheckBroken} isDisabled>
        Done!
      </Button>
    )
  }

  return (
    <Button
      variant="ghost"
      color="var(--dark-80)"
      size={size}
      onPress={openPicker}
      endIcon={frontIcon || FolderImport}
      isDisabled={isPickerLoading}
    >
      {label}
    </Button>
  )
}
